import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { COLORS } from "../../../../shared/theme/colorConfig";
import { configs } from "../../../../shared/config/config";
import { ApplicationData } from "../applicationState";
import type { HouseholdIncomeForm } from "./HouseholdIncome";
import type { AssetsForm } from "./Assets";

// Only include items that generate downstream documentation or verifications
// — skip bookkeeping fields like otherIncomeDescription.
const ASSET_ITEM_KEYS: (keyof AssetsForm)[] = [
  "checking",
  "savings",
  "internetBased",
  "debitCards",
  "moneyMarket",
  "cashOnHand",
  "brokerage",
  "capitalInvestments",
  "stocks",
  "bonds",
  "annuities",
  "cds",
  "cryptocurrency",
  "lifeInsurance",
  "trustFunds",
  "lumpSumAmounts",
  "safetyDepositBox",
  "realEstateProperty",
  "landContracts",
  "taxRefund",
  "crowdsourcing",
  "assetsGivenAway",
  "personalPropertyInvestment",
  "otherAssetsNotListed",
];

const INCOME_ITEM_KEYS: (keyof Omit<
  HouseholdIncomeForm,
  "otherIncomeDescription"
>)[] = [
  "employmentIncome",
  "selfEmployment",
  "cashWork",
  "unemployment",
  "vaGiBill",
  "militaryIncome",
  "publicAssistance",
  "ssRetirement",
  "ssiSsdi",
  "disabilityDeathBenefits",
  "workersComp",
  "cashAssistanceFromOthers",
  "billPaymentHelp",
  "trustsAnnuitiesPensions",
  "realEstatePropertyIncome",
  "onlineIncome",
  "educationAssistance",
  "unearnedIncomeMinors",
  "publicHousingSection8",
  "zeroIncomeMember",
  "otherIncomeNotListed",
];

const incomeFrequencyLabel = (freq?: string): string => {
  switch (freq) {
    case "weekly":
      return "weekly";
    case "bi-weekly":
      return "every two weeks";
    case "semi-monthly":
      return "twice per month";
    case "monthly":
      return "monthly";
    case "annually":
      return "annually";
    case "week":
      return "per week";
    case "month":
      return "per month";
    default:
      return "";
  }
};

export interface FinancialSummaryForm {
  reviewed: true;
}

interface FinancialSummaryProps {
  applicationData: ApplicationData;
  onSubmit: (data: FinancialSummaryForm) => void | Promise<void>;
  onBack: () => void;
  isSaving?: boolean;
}

const SummaryRow: React.FC<{
  title: string;
  description: string;
  accentColor: string;
}> = ({ title, description, accentColor }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
      p: 2,
      mb: 1.5,
      border: `1px solid ${COLORS.gray[200]}`,
      borderRadius: 2,
      backgroundColor: "white",
    }}
  >
    <CheckCircleOutlineIcon
      sx={{ color: accentColor, fontSize: 24, mt: 0.25 }}
    />
    <Box sx={{ flex: 1 }}>
      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.25 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </Box>
);

export const FinancialSummary: React.FC<FinancialSummaryProps> = ({
  applicationData,
  onSubmit,
  onBack,
  isSaving = false,
}) => {
  const { t } = useTranslation();

  const income =
    (applicationData.householdIncome as HouseholdIncomeForm | undefined) ||
    ({} as HouseholdIncomeForm);

  const selectedIncomeKeys = INCOME_ITEM_KEYS.filter((k) => income[k] === true);
  const incomeDescription = (k: keyof typeof income): string => {
    const base = t(
      `affordable.application.householdIncome.items.${k}.description`
    ) as string;
    if (k !== "employmentIncome" || !income.employmentIncomeAmount) return base;
    const frequency = incomeFrequencyLabel(income.employmentIncomeFrequency);
    return `${base} · $${income.employmentIncomeAmount}${
      frequency ? ` ${frequency}` : ""
    }`;
  };

  // Monthly benefit income the applicant declared per source (the inline
  // `<key>Amount` fields on Household Income). Summed here so the applicant
  // sees the rollup before signing; the same total is what the Non-Employed
  // Cert's `unemployment_amount` is prefilled with on the backend. Keys mirror
  // the API's BENEFIT_LABELS (excludes wage-like military).
  const BENEFIT_AMOUNT_KEYS = [
    "unemployment",
    "vaGiBill",
    "publicAssistance",
    "ssRetirement",
    "ssiSsdi",
    "disabilityDeathBenefits",
    "workersComp",
    "trustsAnnuitiesPensions",
  ] as const;
  const incomeRecord = income as unknown as Record<string, unknown>;
  let benefitIncomeTotal = 0;
  let anyBenefitAmount = false;
  for (const k of BENEFIT_AMOUNT_KEYS) {
    if (income[k] !== true) continue;
    const raw = String(incomeRecord[`${k}Amount`] ?? "").trim();
    if (!raw) continue;
    const n = parseFloat(raw);
    if (Number.isFinite(n) && n > 0) {
      benefitIncomeTotal += n;
      anyBenefitAmount = true;
    }
  }
  const benefitIncomeTotalLabel = benefitIncomeTotal.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

  const assets =
    (applicationData.assets as AssetsForm | undefined) || ({} as AssetsForm);
  const selectedAssetKeys = ASSET_ITEM_KEYS.filter((k) => assets[k] === true);

  // Net-family-assets total + HOTMA threshold path. Mirrors the gate in
  // documentRequirements.ts: at/under the threshold the household self-certifies
  // (no asset uploads); over it they upload full verification.
  const assetValues = (assets.assetValues || {}) as Record<
    string,
    { cashValue?: string } | undefined
  >;
  let grossAssetTotal = 0;
  let anyAssetValueCaptured = false;
  for (const [key, detail] of Object.entries(assetValues)) {
    if ((assets as unknown as Record<string, unknown>)[key] !== true) continue;
    const raw = (detail?.cashValue ?? "").trim();
    if (raw) {
      anyAssetValueCaptured = true;
      const n = parseFloat(raw);
      if (Number.isFinite(n)) grossAssetTotal += n;
    }
  }
  // HOTMA: a still-held federal refund is excluded from net family assets for 12
  // months (24 CFR 5.603) — subtract it so this total matches the Assets step's
  // countable figure and the threshold gate keys off the same number.
  const heldRefund =
    assets.federalRefundReceived === true
      ? parseFloat((assets.federalRefundStillHeld ?? "").trim()) || 0
      : 0;
  const assetTotal = Math.max(0, grossAssetTotal - heldRefund);
  const overThreshold = assetTotal > configs.HOTMA_ASSET_THRESHOLD;
  const thresholdLabel = configs.HOTMA_ASSET_THRESHOLD.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  const assetTotalLabel = assetTotal.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

  // Marital status & child-support cert summary — captured in HouseholdIncome
  // step but rendered separately from the income items. Shows the user what
  // they certified before they submit.
  const showMarital = !!income.maritalStatus;
  const needsMaritalCertSummary =
    income.maritalStatus === "Separated" ||
    income.maritalStatus === "Estranged";
  const maritalDetails: string[] = [];
  if (needsMaritalCertSummary) {
    if (income.spouseName) maritalDetails.push(`Spouse: ${income.spouseName}`);
    if (income.receivingSpousalContribution) {
      maritalDetails.push(
        `Receiving spousal contribution${
          income.spousalContributionAmount
            ? `: $${income.spousalContributionAmount}`
            : ""
        }`
      );
    }
    if (income.awardedIncome) {
      maritalDetails.push(
        "Awarded income (alimony / child support / survivor benefits)"
      );
    }
  }

  const csStatus = income.childSupportStatus ?? "";
  const showChildSupportCert = !!csStatus;
  const csChildren = (income.childSupportChildIds ?? []).join(", ");
  const csStatusLabel = csStatus
    ? `(${csStatus}) ${
        (
          {
            a: "Court-ordered, currently receiving",
            b: "Court-ordered, not receiving and not anticipating",
            c: "Not court-ordered, no docs; receives cash",
            d: "Never received; anticipating in next 12 months",
            e: "Never received, not anticipating",
          } as Record<string, string>
        )[csStatus] ?? csStatus
      }`
    : "";
  const csMoneyBits: string[] = [];
  if (income.childSupportAmount) {
    const isMonthly = csStatus === "c" || csStatus === "d";
    const freqLabel =
      csStatus === "a" || csStatus === "b"
        ? income.childSupportFrequency
          ? ` per ${income.childSupportFrequency}`
          : ""
        : isMonthly
        ? " per month"
        : "";
    csMoneyBits.push(`$${income.childSupportAmount}${freqLabel}`);
  }
  const childSupportDetails: string[] = [];
  if (csChildren) childSupportDetails.push(`Custody of: ${csChildren}`);
  childSupportDetails.push(csStatusLabel);
  if (csMoneyBits.length) childSupportDetails.push(...csMoneyBits);

  const giftSourceName = (income.recurringGiftSourceName ?? "").trim();
  const giftSourceEmail = (income.recurringGiftSourceEmail ?? "").trim();
  const showRecurringGift =
    (income.cashAssistanceFromOthers === true ||
      income.billPaymentHelp === true) &&
    !!giftSourceName &&
    !!giftSourceEmail;
  const giftDetails: string[] = [];
  if (giftSourceName) giftDetails.push(giftSourceName);
  if (giftSourceEmail) giftDetails.push(giftSourceEmail);
  if ((income.recurringGiftSourcePhone ?? "").trim()) {
    giftDetails.push((income.recurringGiftSourcePhone ?? "").trim());
  }

  const totalCount = selectedIncomeKeys.length + selectedAssetKeys.length;
  const hasItems =
    totalCount > 0 || showMarital || showChildSupportCert || showRecurringGift;

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: { xs: 3, sm: 5 } }}>
      <Box sx={{ maxWidth: 720, mx: "auto" }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: 24, sm: 32 },
            textAlign: "center",
            mb: 1.5,
          }}
        >
          {t("affordable.application.financialSummary.title")}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ textAlign: "center", mb: 4 }}
        >
          {t(
            totalCount === 1
              ? "affordable.application.financialSummary.subtitleSingular"
              : "affordable.application.financialSummary.subtitlePlural",
            { count: totalCount }
          )}
        </Typography>

        {!hasItems && (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              border: `1px dashed ${COLORS.gray[300]}`,
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("affordable.application.financialSummary.emptyState")}
            </Typography>
          </Box>
        )}

        {selectedIncomeKeys.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.gray[600],
                fontWeight: 700,
                letterSpacing: 0.8,
                mb: 1.5,
              }}
            >
              {t("affordable.application.financialSummary.sectionIncome")}
            </Typography>
            {selectedIncomeKeys.map((k) => (
              <SummaryRow
                key={k}
                title={t(
                  `affordable.application.householdIncome.items.${k}.title`
                )}
                description={incomeDescription(k)}
                accentColor={COLORS.green[500]}
              />
            ))}
            {(income.additionalJobs || []).map((job, index) => {
              const frequency = incomeFrequencyLabel(job.frequency);
              const kindLabel = t(
                `affordable.application.householdIncome.additionalJobs.kindOptions.${job.kind}`
              );
              return (
                <SummaryRow
                  key={job.id || `additional-job-${index}`}
                  title={t(
                    "affordable.application.householdIncome.additionalJobs.jobLabel",
                    { n: index + 2 }
                  )}
                  description={`${kindLabel}${
                    job.employerName ? ` · ${job.employerName}` : ""
                  }${job.amount ? ` · $${job.amount}` : ""}${
                    frequency ? ` ${frequency}` : ""
                  }`}
                  accentColor={COLORS.green[500]}
                />
              );
            })}
            {anyBenefitAmount && (
              <Box
                sx={{
                  mt: 0.5,
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${COLORS.success.main}`,
                  backgroundColor: `${COLORS.success.main}14`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "affordable.application.financialSummary.benefitsIncomeLabel"
                  )}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {`${benefitIncomeTotalLabel} ${t(
                    "affordable.application.financialSummary.perMonthSuffix"
                  )}`}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {(showMarital || showChildSupportCert || showRecurringGift) && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.gray[600],
                fontWeight: 700,
                letterSpacing: 0.8,
                mb: 1.5,
              }}
            >
              Marital & Family
            </Typography>
            {showMarital && (
              <SummaryRow
                title={`Marital status: ${income.maritalStatus}`}
                description={maritalDetails.join(" · ") || "—"}
                accentColor={COLORS.darkBlue[500]}
              />
            )}
            {showChildSupportCert && (
              <SummaryRow
                title="Child Support Certification"
                description={childSupportDetails.filter(Boolean).join(" · ")}
                accentColor={COLORS.darkBlue[500]}
              />
            )}
            {showRecurringGift && (
              <SummaryRow
                title="Recurring Gift Source"
                description={giftDetails.filter(Boolean).join(" · ")}
                accentColor={COLORS.darkBlue[500]}
              />
            )}
          </Box>
        )}

        {selectedAssetKeys.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.gray[600],
                fontWeight: 700,
                letterSpacing: 0.8,
                mb: 1.5,
              }}
            >
              {t("affordable.application.financialSummary.sectionAssets")}
            </Typography>
            {selectedAssetKeys.map((k) => (
              <SummaryRow
                key={k}
                title={t(`affordable.application.assets.items.${k}.title`)}
                description={t(
                  `affordable.application.assets.items.${k}.description`
                )}
                accentColor={COLORS.blue[500]}
              />
            ))}
            {anyAssetValueCaptured && (
              <Box
                sx={{
                  mt: 0.5,
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${
                    overThreshold ? COLORS.warning.main : COLORS.success.main
                  }`,
                  backgroundColor: overThreshold
                    ? `${COLORS.warning.main}14`
                    : `${COLORS.success.main}14`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t("affordable.application.assets.value.runningTotalLabel")}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {assetTotalLabel}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    color: overThreshold
                      ? COLORS.warning.main
                      : COLORS.success.main,
                    fontWeight: 600,
                  }}
                >
                  {overThreshold
                    ? t("affordable.application.assets.value.overThreshold", {
                        threshold: thresholdLabel,
                      })
                    : t("affordable.application.assets.value.underThreshold", {
                        threshold: thresholdLabel,
                      })}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {hasItems && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              p: 2,
              backgroundColor: COLORS.blue[50],
              border: `1px solid ${COLORS.blue[100]}`,
              borderRadius: 2,
              mb: 4,
            }}
          >
            <InfoOutlinedIcon
              sx={{ color: COLORS.darkBlue[500], fontSize: 20, mt: 0.25 }}
            />
            <Typography variant="body2" color="text.secondary">
              {t("affordable.application.financialSummary.verificationNotice")}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 4,
          mb: 2,
          borderTop: `1px solid ${COLORS.gray[200]}`,
          pt: 3,
        }}
      >
        <Button
          variant="text"
          onClick={onBack}
          disabled={isSaving}
          startIcon={<ArrowBackIcon />}
          sx={{ color: "text.primary" }}
        >
          {t("affordable.application.assets.back")}
        </Button>
        <Button
          variant="contained"
          onClick={() => onSubmit({ reviewed: true })}
          disabled={isSaving}
          endIcon={
            isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <ArrowForwardIcon />
            )
          }
        >
          {t("affordable.application.assets.continue")}
        </Button>
      </Box>
    </Box>
  );
};
